import { useEffect, useMemo, useRef, useState } from "react";
import SEO from "../components/SEO";
import { baseUrl } from "../config";
import { useNavigate } from "react-router-dom";
import {
    FiSearch, FiGrid, FiList, FiCalendar, FiPlusCircle, FiFolderPlus,
    FiArrowRight, FiArrowLeft, FiCheck, FiChevronDown, FiX,
} from "react-icons/fi";

// ✅ Import your existing ProjectSelector component
import ProductionToolsDashboard from '../pages/ProjectSelector';
import ProjectInitializer from '../pages/ProjectStarter';
import headerBg from '../assets/img/toolmanagerheader.jpg';
import '../styles/toolmanager.css';

const TYPE_LABELS = { advertisement: 'Advertisement', short: 'Short film', full: 'Long film' };
const INITIAL_VISIBLE = 6;

// Date field name isn't guaranteed across project records — use whichever exists
const getProjectDate = (p) =>
    p.created_at || p.createdAt || p.creation_date || p.created_on || p.date_created || null;

const formatDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

const sortValue = (p) => {
    const d = getProjectDate(p);
    const t = d ? new Date(d).getTime() : NaN;
    return isNaN(t) ? Number(p.project_id) || 0 : t;
};

export default function ToolManager({ onProjectChange, resetSignal } = {}) {
    const [activeUser, setActiveUser] = useState(null);
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [projectsLoading, setProjectsLoading] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [projectDropdownOpen, setProjectDropdownOpen] = useState(false); // legacy dropdown state (dropdown replaced by cards)
    const [currentView, setCurrentView] = useState('tool-manager'); // 'tool-manager' or 'create-project'

    // UI-only state for the new project browser
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('recent');
    const [viewMode, setViewMode] = useState('grid');
    const [showAll, setShowAll] = useState(false);
    const rootRef = useRef(null);

    const navigate = useNavigate();

    // Fetch user ID
    const getUser = async () => {
        try {
            const response = await fetch(`${baseUrl}/getUserIDByToken`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
            });

            if (!response.ok) {
                navigate("/login");
                return;
            }

            const data = await response.json();
            setActiveUser(data.user_id);
            return data.user_id;
        } catch (err) {
            navigate("/login");
        }
    };

    // Fetch user's projects
    const getProjects = async () => {
        try {
            setProjectsLoading(true);
            const userId = await getUser();

            const projectIdsResponse = await fetch(`${baseUrl}/getProjectsByUserId`, {
                headers: { "user_id": userId }
            });

            if (projectIdsResponse.ok) {
                const projectIdsData = await projectIdsResponse.json();

                let projectInfoTemp = [];
                for (let i = 0; i < projectIdsData.length; i++) {
                    const element = projectIdsData[i];
                    const projInfRequest = await fetch(`${baseUrl}/getProjectById`, {
                        headers: { "project_id": element.project_id }
                    });

                    if (projInfRequest.ok) {
                        const projInfResponse = await projInfRequest.json();
                        projectInfoTemp.push(projInfResponse);
                    }
                }

                setProjects(projectInfoTemp);
            }
        } catch (err) {
        } finally {
            setProjectsLoading(false);
            setLoading(false);
        }
    };

    // Handle project selection
    const handleProjectSelect = (project) => {
        setSelectedProject(project);
        setProjectDropdownOpen(false);
    };

    // Handle successful project creation
    const handleProjectCreated = async () => {
        setCurrentView('tool-manager');
        await getProjects();
    };

    useEffect(() => {
        getProjects();
    }, []);

    // Selecting / clearing a project switches screens — start each screen at the top
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [selectedProject]);

    // Tell the parent dashboard which project is open (drives the breadcrumb)
    useEffect(() => {
        if (onProjectChange) onProjectChange(selectedProject);
    }, [selectedProject, onProjectChange]);

    // Clear the parent's breadcrumb when Tool Manager unmounts
    useEffect(() => {
        return () => { if (onProjectChange) onProjectChange(null); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Parent asks to go back to the project list (breadcrumb "Tool Manager" / sidebar click)
    useEffect(() => {
        if (resetSignal) setSelectedProject(null);
    }, [resetSignal]);

    const clearSelection = () => {
        setSelectedProject(null);
    };

    const filteredProjects = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        const list = term
            ? projects.filter(p =>
                [p.title, p.project_desc, p.genre]
                    .filter(Boolean)
                    .some(v => String(v).toLowerCase().includes(term)))
            : [...projects];

        if (sortBy === 'name') {
            list.sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
        } else if (sortBy === 'oldest') {
            list.sort((a, b) => sortValue(a) - sortValue(b));
        } else {
            list.sort((a, b) => sortValue(b) - sortValue(a));
        }
        return list;
    }, [projects, searchTerm, sortBy]);

    const isSearching = searchTerm.trim().length > 0;
    const displayedProjects = showAll || isSearching
        ? filteredProjects
        : filteredProjects.slice(0, INITIAL_VISIBLE);
    const hiddenCount = filteredProjects.length - displayedProjects.length;

    const renderProjectCard = (project) => {
        const isSelected = selectedProject?.project_id === project.project_id;
        const date = formatDate(getProjectDate(project));
        const typeLabel = TYPE_LABELS[project.project_type];

        return (
            <button
                type="button"
                key={project.project_id}
                onClick={() => handleProjectSelect(project)}
                className={`tm-card ${isSelected ? 'is-selected' : ''}`}
                aria-pressed={isSelected}
            >
                <div className="tm-card-top">
                    <h3 className="tm-card-title">{project.title}</h3>
                    {isSelected && <span className="tm-card-check"><FiCheck size={14} /></span>}
                </div>
                {project.genre && <p className="tm-card-genre">{project.genre}</p>}
                <p className="tm-card-desc">{project.project_desc || 'No description'}</p>
                {(date || typeLabel) && (
                    <div className="tm-card-meta">
                        {date && <span className="tm-card-date"><FiCalendar size={13} /> {date}</span>}
                        {typeLabel && <span className="tm-chip">{typeLabel}</span>}
                    </div>
                )}
            </button>
        );
    };

    // Render content based on currentView
    const renderContent = () => {
        if (currentView === 'create-project') {
            return (
                <div className="tm-root tm-create">
                    <div className="tm-create-bar">
                        <button type="button" onClick={() => setCurrentView('tool-manager')} className="tm-back">
                            <FiArrowLeft size={16} /> Back to Tool Manager
                        </button>
                    </div>
                    <ProjectInitializer onProjectCreated={handleProjectCreated} />
                </div>
            );
        }

        // Project screen: opens after a project is picked
        if (selectedProject) {
            const projectDate = formatDate(getProjectDate(selectedProject));
            return (
                <div className="tm-root tm-project">
                    <SEO
                      title="Tool Manager"
                      description="Access all your ProducerBuddy film production tools from one central tool manager dashboard."
                      url="https://producerbuddy.pilomation.com/tool-manager"
                      robots="noindex, nofollow"
                    />
                    <header className="tm-hero tm-hero--project">
                        <div className="tm-hero-bg" style={{ backgroundImage: `url(${headerBg})` }} />
                        <div className="tm-hero-overlay" />
                        <div className="tm-hero-content">
                            <button type="button" onClick={clearSelection} className="tm-hero-back">
                                <FiArrowLeft size={15} /> All projects
                            </button>
                            <h1 className="tm-project-title">{selectedProject.title}</h1>
                            {selectedProject.genre && <p className="tm-project-genre">{selectedProject.genre}</p>}
                            {selectedProject.project_desc && <p className="tm-project-desc">{selectedProject.project_desc}</p>}
                            {projectDate && (
                                <p className="tm-project-date"><FiCalendar size={14} /> {projectDate}</p>
                            )}
                        </div>
                    </header>

                    {/* ✅ Embed ProductionToolsDashboard (your existing ProjectSelector) — it handles its own padding */}
                    <ProductionToolsDashboard projectId={selectedProject.project_id} />
                </div>
            );
        }

        // Default: tool-manager view
        return (
            <div className="tm-root" ref={rootRef}>
                <SEO
                  title="Tool Manager"
                  description="Access all your ProducerBuddy film production tools from one central tool manager dashboard."
                  url="https://producerbuddy.pilomation.com/tool-manager"
                  robots="noindex, nofollow"
                />

                {/* Header banner */}
                <header className="tm-hero">
                    <div className="tm-hero-bg" style={{ backgroundImage: `url(${headerBg})` }} />
                    <div className="tm-hero-overlay" />
                    <div className="tm-hero-content">
                        <h1 className="tm-hero-title">Select a project</h1>
                        <p className="tm-hero-sub">Choose a project to access your production tools, resources, and collaborations.</p>
                    </div>
                </header>

                <div className="tm-body">
                    {projectsLoading ? (
                        <div className="tm-grid">
                            <div className="tm-skeleton" />
                            <div className="tm-skeleton" />
                            <div className="tm-skeleton" />
                        </div>
                    ) : projects.length === 0 ? (
                        <div className="tm-empty">
                            <span className="tm-empty-icon"><FiFolderPlus size={28} /></span>
                            <p className="tm-empty-title">No projects yet</p>
                            <p className="tm-empty-sub">Create your first project to unlock your production tools.</p>
                            <button type="button" onClick={() => setCurrentView('create-project')} className="tm-btn tm-btn-primary">
                                <FiFolderPlus size={16} /> Create new project
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Toolbar */}
                            <div className="tm-toolbar">
                                <div className="tm-search">
                                    <FiSearch size={16} className="tm-search-icon" />
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Search your projects..."
                                        className="tm-search-input"
                                        aria-label="Search projects"
                                    />
                                    {isSearching && (
                                        <button type="button" className="tm-search-clear" onClick={() => setSearchTerm('')} aria-label="Clear search">
                                            <FiX size={14} />
                                        </button>
                                    )}
                                </div>

                                <div className="tm-toolbar-right">
                                    <div className="tm-select-wrap">
                                        <select
                                            value={sortBy}
                                            onChange={(e) => setSortBy(e.target.value)}
                                            className="tm-select"
                                            aria-label="Sort projects"
                                        >
                                            <option value="recent">Recently created</option>
                                            <option value="oldest">Oldest first</option>
                                            <option value="name">Name (A–Z)</option>
                                        </select>
                                        <FiChevronDown size={16} className="tm-select-icon" />
                                    </div>

                                    <div className="tm-toggle" role="group" aria-label="View mode">
                                        <button
                                            type="button"
                                            className={viewMode === 'grid' ? 'is-active' : ''}
                                            onClick={() => setViewMode('grid')}
                                            aria-pressed={viewMode === 'grid'}
                                            aria-label="Grid view"
                                        >
                                            <FiGrid size={16} />
                                        </button>
                                        <button
                                            type="button"
                                            className={viewMode === 'list' ? 'is-active' : ''}
                                            onClick={() => setViewMode('list')}
                                            aria-pressed={viewMode === 'list'}
                                            aria-label="List view"
                                        >
                                            <FiList size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Section head */}
                            <div className="tm-section-head">
                                <h2 className="tm-section-title">
                                    Your projects <span className="tm-count">{filteredProjects.length}</span>
                                </h2>
                                {!isSearching && filteredProjects.length > INITIAL_VISIBLE && (
                                    <button type="button" className="tm-link" onClick={() => setShowAll(!showAll)}>
                                        {showAll ? 'Show less' : `View all (${filteredProjects.length})`}
                                        {!showAll && <FiArrowRight size={14} />}
                                    </button>
                                )}
                            </div>

                            {/* Projects */}
                            {filteredProjects.length === 0 ? (
                                <div className="tm-nomatch">
                                    <p>No projects match "{searchTerm.trim()}".</p>
                                    <button type="button" className="tm-link" onClick={() => setSearchTerm('')}>Clear search</button>
                                </div>
                            ) : (
                                <div className={viewMode === 'list' ? 'tm-list' : 'tm-grid'}>
                                    {displayedProjects.map(renderProjectCard)}
                                </div>
                            )}

                            {hiddenCount > 0 && (
                                <button type="button" className="tm-more" onClick={() => setShowAll(true)}>
                                    Show {hiddenCount} more {hiddenCount === 1 ? 'project' : 'projects'}
                                </button>
                            )}

                            {/* Create prompt */}
                            <div className="tm-create-card">
                                <div className="tm-create-text">
                                    <FiPlusCircle size={24} className="tm-create-icon" />
                                    <p className="tm-create-title">Don't see your project?</p>
                                    <p className="tm-create-sub">Create a new project and start bringing your vision to life.</p>
                                </div>
                                <button type="button" onClick={() => setCurrentView('create-project')} className="tm-btn tm-btn-primary tm-create-btn">
                                    <FiFolderPlus size={16} /> Create new project
                                </button>
                            </div>
                        </>
                    )}

                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="tm-root tm-loading">
                <div className="tm-spinner" />
                <p>Loading projects...</p>
            </div>
        );
    }

    return renderContent();
}